if (!customElements.get("main-header")) {
  /**
   * The main header component used globally
   * @extends {HTMLElement}
   */
  class MainHeader extends HTMLElement {
    constructor() {
      super();

      /** @type {NodeListOf<HTMLButtonElement|HTMLAnchorElement>} */
      this.menuButtons = this.querySelectorAll(".header__menu-btn");

      /** @type {NodeListOf<HTMLElement>} */
      this.subMenus = this.querySelectorAll(".header__sub");

      /** @type {HTMLElement} */
      // @ts-ignore
      this.subMenusContainer = this.querySelector(".header__sub-list");

      /** @type {HTMLButtonElement|null} */
      this.activeMenu = null;

      // Events
      this.onMouseoverBtn = this.onMouseoverBtn.bind(this);
      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
      this.onClickMenuBtn = this.onClickMenuBtn.bind(this);
      this.onMenuBtnKeydown = this.onMenuBtnKeydown.bind(this);
      this.onSubmenuKeydown = this.onSubmenuKeydown.bind(this);
      this.onSubmenuFocusOut = this.onSubmenuFocusOut.bind(this);

      this.leaveTimer = null;
      this.hoverTimer = null;
    }

    connectedCallback() {
      this.menuButtons.forEach((btn) => {
        btn.addEventListener("mouseover", this.onMouseoverBtn);
        btn.addEventListener("click", this.onClickMenuBtn);
        // @ts-ignore
        btn.addEventListener("keydown", this.onMenuBtnKeydown);
      });

      this.subMenus.forEach((submenu) => {
        submenu.addEventListener("keydown", this.onSubmenuKeydown);
        submenu.addEventListener("focusout", this.onSubmenuFocusOut);
      });

      this.addEventListener("mouseenter", this.onMouseEnter);
      this.addEventListener("mouseleave", this.onMouseLeave);
    }

    disconnectedCallback() {
      this.menuButtons.forEach((btn) => {
        btn.removeEventListener("mouseover", this.onMouseoverBtn);
        btn.addEventListener("click", this.onClickMenuBtn);
        // @ts-ignore
        btn.removeEventListener("keydown", this.onMenuBtnKeydown);
      });
      this.subMenus.forEach((submenu) => {
        submenu.removeEventListener("keydown", this.onSubmenuKeydown);
        submenu.removeEventListener("focusout", this.onSubmenuFocusOut);
      });
      this.removeEventListener("mouseenter", this.onMouseEnter);
      this.removeEventListener("mouseleave", this.onMouseLeave);
    }

    /** @param {KeyboardEvent} e */
    onMenuBtnKeydown(e) {
      /** @type {HTMLButtonElement} */
      // @ts-expect-error
      const btn = e.currentTarget;

      if (e.key === "Enter" || e.key === " ") {
        const index = Number(btn.dataset.index);
        this.activeMenu = btn;
        this.expandMenu(index, true);
        return;
      }
    }

    /** @param {FocusEvent} e */
    onSubmenuFocusOut(e) {
      const submenu = e.currentTarget;
      const relatedTarget = e.relatedTarget;
      if (!(submenu instanceof HTMLElement)) {
        return;
      }

      if (!(relatedTarget instanceof HTMLElement)) {
        return;
      }

      setTimeout(() => {
        const isOutside = !submenu.contains(relatedTarget);
        if (isOutside) {
          this.collapseMenu(false, true);
        }
      });
    }

    /** @param {KeyboardEvent} e */
    onSubmenuKeydown(e) {
      if (e.key === "Escape" || e.key === "esc") {
        this.collapseMenu(false, true);
        return;
      }
    }

    /** @param {Event} e */
    onClickMenuBtn(e) {
      /** @type {HTMLButtonElement} */
      // @ts-expect-error
      const menuBtn = e.target;
      const index = Number(menuBtn.dataset.index);
      this.activeMenu = menuBtn;
      this.expandMenu(index, true);
    }

    /**
     * @param {Event} e
     * @returns {void}
     */
    onMouseoverBtn(e) {
      /** @type {HTMLButtonElement} */
      // @ts-ignore
      const btn = e.currentTarget;

      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer);
        this.hoverTimer = null;
      }

      if (this.activeMenu === btn) {
        return;
      }

      // If we have a menu already don't animate top level
      const keepMainHeaderOpen = this.activeMenu !== null;
      const toggleDelay = keepMainHeaderOpen ? 300 : 200;

      this.hoverTimer = window.setTimeout(() => {
        this.collapseMenu(keepMainHeaderOpen);
        this.activeMenu = btn;

        const menuIndex = parseInt(this.activeMenu.dataset.index || "-1", 10);

        if (menuIndex === -1) {
          return;
        }

        this.expandMenu(menuIndex);
      }, toggleDelay);
    }

    onMouseEnter() {
      if (this.leaveTimer) {
        clearTimeout(this.leaveTimer);
        this.leaveTimer = null;
      }
    }

    onMouseLeave() {
      this.leaveTimer = window.setTimeout(() => {
        this.collapseMenu();
        this.leaveTimer = null;
      }, 300);
    }

    /**
     * @param {number} index
     */
    expandMenu(index, focusFirstItem = false) {
      const menuBtn = this.menuButtons[index];
      menuBtn.setAttribute("aria-expanded", "true");

      const submenu = this.subMenus[index];
      submenu.classList.add("is-open");

      const type = submenu.dataset.type;

      // For menu products set the first item as selected when we expand
      if (type == "menu_products") {
        submenu.querySelector("product-menu")?.setAttribute("open", "true");
      }

      /** @type {HTMLElement|null} */
      // @ts-ignore
      let link = null;

      if (focusFirstItem) {
        link =
          submenu.querySelector(".header__sub-nested-menu-link") ||
          submenu.querySelector(".header__sub-content-block");
      }

      if (link) {
        link.focus();
      }
    }

    collapseMenu(keepMainHeaderOpen = false, focusHeader = false) {
      const submenu = this.getActiveSubmenu();

      if (submenu && keepMainHeaderOpen === false) {
        this.subMenusContainer.classList.add("is-closing");
        this.subMenusContainer.addEventListener(
          "animationend",
          () => {
            submenu.classList.remove("is-open");
            this.subMenusContainer.classList.remove("is-closing");
          },
          { once: true }
        );

        const index = Number(submenu.dataset.submenu);
        const menuBtn = this.menuButtons[index];
        menuBtn.setAttribute("aria-expanded", "false");
      } else if (submenu && keepMainHeaderOpen === true) {
        submenu.classList.remove("is-open");
      }

      this.querySelectorAll('product-menu[open="true"]').forEach((x) =>
        x.setAttribute("open", "false")
      );

      this.activeMenu = null;

      if (focusHeader) {
        const index = Number(submenu?.dataset.submenu);
        const menu = this.menuButtons[index];
        menu.focus();
      }
    }

    getActiveSubmenu() {
      if (this.activeMenu) {
        const menuIndex = parseInt(this.activeMenu.dataset.index || "-1", 10);
        return this.subMenus[menuIndex];
      }
      return null;
    }
  }

  //
  // ProductMenu
  //
  class ProductMenu extends HTMLElement {
    constructor() {
      super();

      /** @type {HTMLElement|null} */
      this.activeMenu = null;

      /** @type {HTMLElement|null} */
      this.activeLink = null;

      /** @type {HTMLElement|null} */
      this.hoverLink = null;

      /** @type NodeListOf<HTMLElement> */
      // @ts-ignore
      this.links = [];

      /** @type NodeListOf<HTMLElement> */
      // @ts-ignore
      this.blocks = [];

      // Events
      this.onMouseenterLink = this.onMouseenterLink.bind(this);
      this.onMouseleaveLink = this.onMouseleaveLink.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      // Other
      this.timer = null;
      this.isInitialized = false;

      this.addEventListener("keydown", this.onKeydown);
    }

    static get observedAttributes() {
      return ["open"];
    }

    /**
     * @param {string} name
     * @param {string} oldValue
     * @param {string} newValue
     */
    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== "open") {
        return;
      }

      if (oldValue === newValue) {
        return;
      }

      if (newValue === "true") {
        this.openMenu();
        return;
      }
      this.closeMenu();
    }

    connectedCallback() {
      this.closeMenu();
    }

    initialize() {
      if (this.isInitialized) {
        return;
      }

      /** @type {NodeListOf<HTMLElement>} */
      this.links = this.querySelectorAll(".header__sub-nested-menu-link");

      /** @type {NodeListOf<HTMLElement>} */
      this.blocks = this.querySelectorAll(".header__sub-nested-blocks");

      /** @type {HTMLElement} */
      // @ts-ignore
      this.menuContainer = this.querySelector(".header__sub-nested-menu");

      this.isInitialize = true;
    }

    /** @param {KeyboardEvent} e */
    onKeydown(e) {
      /** @type {HTMLElement} */
      // @ts-expect-error
      const target = e.target;
      const key = e.key;

      if (key === "Enter" || key === " ") {
        if (target.tagName === "A") {
          return;
        }

        const index = Number(target.dataset.index);
        this.setActiveSubmenu(index);
      }
    }

    openMenu() {
      this.initialize();
      this.setActiveSubmenu(0);

      this.links.forEach((x) => {
        x.addEventListener("mouseenter", this.onMouseenterLink);
        x.addEventListener("mouseleave", this.onMouseleaveLink);
      });
    }

    closeMenu() {
      this.links.forEach((x) => {
        x.removeEventListener("mouseenter", this.onMouseenterLink);
        x.removeEventListener("mouseleave", this.onMouseleaveLink);
      });
    }

    /**
     * @param {number} index
     */
    setActiveSubmenu(index) {
      this.links.forEach((x) => x.classList.remove("is-active"));
      this.blocks.forEach((x) => x.classList.remove("is-active"));
      this.blocks.forEach((x) => x.classList.remove("is-visible"));

      const link = this.links[index];
      const block = this.blocks[index];

      link.classList.add("is-active");
      block.classList.add("is-active");

      this.activeLink = link;

      window.setTimeout(() => {
        block.classList.add("is-visible");
      }, 50);
    }

    /**
     * @param {Event} e
     */
    onMouseenterLink(e) {
      /** @type {HTMLElement} */
      // @ts-ignore
      this.hoverLink = e.currentTarget;

      this.timer = setTimeout(() => {
        if (this.hoverLink) {
          const index = parseInt(this.hoverLink.dataset.index || "0");
          this.setActiveSubmenu(index);
        }
      }, 200);
    }

    onMouseleaveLink() {
      /** @type {HTMLElement} */
      // @ts-ignore
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    }
  }

  class HeaderSearch extends HTMLElement {
    constructor() {
      super();

      /** @type {HTMLFormElement} */
      // @ts-ignore
      this.form = this.querySelector(".header__search");

      /** @type {HTMLFormElement} */
      // @ts-ignore
      this.searchBtn = this.querySelector(".header__search-btn");

      /** @type {HTMLInputElement} */
      // @ts-ignore
      this.input = this.querySelector(".header__search-input");

      /** @type {HTMLFormElement} */
      // @ts-ignore
      this.parentHeader = this.closest(".header");

      this.handleSearchBtnClick = this.handleSearchBtnClick.bind(this);
      this.handleOutsideClick = this.handleOutsideClick.bind(this);
      this.handleFormClick = this.handleFormClick.bind(this);
      this.handleFormKeyDown = this.handleFormKeyDown.bind(this);
    }

    connectedCallback() {
      this.form.addEventListener("click", this.handleFormClick);
      this.searchBtn.addEventListener("click", this.handleSearchBtnClick);
    }

    disconnectedCallback() {
      this.form.remvoeEventListener("click", this.handleFormClick);
      this.searchBtn.removeEventListener("click", this.handleSearchBtnClick);
    }

    /**
     * @param {Event} e
     */
    handleSearchBtnClick(e) {
      e.preventDefault();

      if (this.parentHeader.classList.contains("has-search") === false) {
        this.openSearch();
        return;
      }

      if (this.input.value === "") {
        this.closeSearch();
        return;
      }

      this.submitting = true;
      this.form.submit();
    }

    /**
     * @param {Event} e
     */
    handleFormClick(e) {
      e.stopPropagation();
    }

    openSearch() {
      this.parentHeader.classList.add("has-search");
      this.input.tabIndex = 0;
      this.input.setAttribute("aria-hidden", "false");
      this.input.focus();
      document.body.addEventListener("click", this.handleOutsideClick);
      this.form.addEventListener("keydown", this.handleFormKeyDown);
    }

    closeSearch() {
      this.parentHeader.classList.remove("has-search");
      this.input.value = "";
      this.input.setAttribute("aria-hidden", "true");
      this.input.tabIndex = -1;

      document.body.removeEventListener("click", this.handleOutsideClick);
      this.form.removeEventListener("keydown", this.handleFormKeyDown);

      if (document.activeElement === this.input) {
        this.searchBtn.focus();
      }
    }

    /**
     * @param {KeyboardEvent} e
     */
    handleFormKeyDown(e) {
      if (e.key === "Escape") {
        this.closeSearch();
        return;
      }
    }

    handleOutsideClick() {
      this.closeSearch();
    }
  }

  class HeaderDrawer extends HTMLElement {
    constructor() {
      super();

      /** @type {HTMLElement} */
      // @ts-ignore
      this.rootMenu = this.querySelector(".header-drawer.is-root");

      /** @type {HTMLElement} */
      // @ts-ignore
      this.searchDrawer = this.querySelector(".header-drawer.is-search");

      /** @type {HTMLElement[]} */
      this.drawers = [];

      /** @type {HTMLElement[]} */
      this.links = [];

      /** @type {HTMLElement[]} */
      this.closeButtons = [];

      /** @type {HTMLElement[]} */
      this.backButtons = [];

      /** @type {HTMLElement} */
      // @ts-ignore
      this.toggleBtn = document.querySelector(".header__toggle-mobile-menu");
      this.searchExpandBtn = document.querySelector(
        ".header__search-expand-btn"
      );

      this.handleToggleBtnClick = this.handleToggleBtnClick.bind(this);
      this.handleCloseBtnClick = this.handleCloseBtnClick.bind(this);
      this.handleBackBtnClick = this.handleBackBtnClick.bind(this);
      this.handleSearchExpandBtnClick =
        this.handleSearchExpandBtnClick.bind(this);

      this.initialize();
    }

    connectedCallback() {
      this.toggleBtn.addEventListener("click", this.handleToggleBtnClick);
      this.searchExpandBtn?.addEventListener(
        "click",
        this.handleSearchExpandBtnClick
      );
    }

    disconnectedCallback() {
      this.toggleBtn.removeEventListener("click", this.handleToggleBtnClick);
      this.searchExpandBtn?.removeEventListener(
        "click",
        this.handleSearchExpandBtnClick
      );

      if (!this.isInitialized) {
        return;
      }

      this.closeButtons.forEach((x) =>
        x.removeEventListener("click", this.handleCloseBtnClick)
      );

      this.links.forEach((x) =>
        x.removeEventListener("click", this.handleLinkClick)
      );

      this.backButtons.forEach((x) => {
        x.removeEventListener("click", this.handleBackBtnClick);
      });
    }

    initialize() {
      if (this.isInitialized) {
        return;
      }

      this.drawers = Array.from(this.querySelectorAll(".header-drawer"));
      this.links = Array.from(this.querySelectorAll(".header-drawer__link"));

      this.backButtons = Array.from(
        this.querySelectorAll(".header-drawer__back-btn")
      );

      this.closeButtons = Array.from(
        this.querySelectorAll(".header-drawer__close-btn")
      );

      this.closeButtons.forEach((x) =>
        x.addEventListener("click", this.handleCloseBtnClick)
      );

      this.backButtons.forEach((x) =>
        x.addEventListener("click", this.handleBackBtnClick)
      );

      this.links.forEach((x) =>
        x.addEventListener("click", this.handleLinkClick)
      );

      this.isInitialized = true;
    }

    handleCloseBtnClick() {
      this.drawers.forEach((x) => x.classList.remove("is-open"));
      document.body.classList.remove("has-open-menu");
    }

    /**
     * @param {Event} e
     */
    handleLinkClick(e) {
      /** @type {HTMLElement} */
      // @ts-ignore
      const link = e.currentTarget;
      const sibling = link.nextElementSibling;

      if (sibling) {
        sibling.classList.add("is-open");
      }
    }

    /**
     * @param {Event} e
     */
    handleBackBtnClick(e) {
      /** @type {HTMLElement} */
      // @ts-ignore
      const btn = e.currentTarget;

      const drawer = btn.closest(".header-drawer");

      if (drawer) {
        drawer.classList.remove("is-open");
      }
    }

    handleToggleBtnClick() {
      this.initialize();

      const isOpen = this.rootMenu.classList.contains("is-open");

      if (isOpen) {
        document.body.classList.remove("has-open-menu");
        this.rootMenu.classList.remove("is-open");
      } else {
        this.rootMenu.classList.add("is-open");
        document.body.classList.add("has-open-menu");
      }
    }

    handleSearchExpandBtnClick() {
      this.searchDrawer.classList.add("is-open");
    }
  }

  customElements.define("main-header", MainHeader);
  customElements.define("product-menu", ProductMenu);
  customElements.define("header-search", HeaderSearch);
  customElements.define("header-drawer", HeaderDrawer);
}
